import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import Map, { Marker, useMap } from "react-map-gl/maplibre";
import Supercluster from "supercluster";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaArrowRight, FaCrosshairs, FaStar, FaRegStar } from "react-icons/fa";
import { BsLayers } from "react-icons/bs";
import "maplibre-gl/dist/maplibre-gl.css";
import { CATEGORIES, CAT_COLORS } from "../../data/mockParticipants";
import { storiesAPI } from "../../services/api";
import CircularBadge from "../badge";
import "./CommunityMap.css";
/* ── Map style — free Stadia Maps topo/dark tile ─────────────
   No API key required for reasonable usage.                  */
const MAP_STYLE = {
  version: 8,
  sources: {
    esri: {
      type: "raster",
      tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
      tileSize: 256,
      attribution: "Esri",
    },
  },
  layers: [
    {
      id: "esri",
      type: "raster",
      source: "esri",
    },
  ],
};

/* ── Initial viewport ─────────────────────────────────────── */
const INITIAL_VIEW = { longitude: -95, latitude: 42, zoom: 3.4 };

/* ── Star rating renderer ─────────────────────────────────── */
function Stars({ rating }) {
  return (
    <span className="cmap__stars" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (n <= rating ? <FaStar key={n} className="cmap__star cmap__star--filled" /> : <FaRegStar key={n} className="cmap__star" />))}
    </span>
  );
}

/* ── Category icon SVGs ───────────────────────────────────── */
function CatIcon({ catId, size = 16 }) {
  const icons = {
    veteran: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L9 9H2l5.5 4-2 7L12 16l6.5 4-2-7L22 9h-7z" />
      </svg>
    ),
    first_responder: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C8 2 5 6 5 10c0 5.25 7 12 7 12s7-6.75 7-12c0-4-3-8-7-8zm0 11a3 3 0 110-6 3 3 0 010 6z" />
      </svg>
    ),
    law_enforcement: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5L12 1z" />
      </svg>
    ),
    gold_star: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    volunteer: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
      </svg>
    ),
    donor: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
    ),
  };
  return icons[catId] || null;
}

/* ── Custom map pin ───────────────────────────────────────── */
function Pin({ color, catId, size = 32 }) {
  return (
    <svg width={size} height={size * 1.4} viewBox="0 0 32 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.16 0 0 7.16 0 16c0 11.31 14.29 26.36 15.24 27.38a1.07 1.07 0 001.52 0C17.71 42.36 32 27.31 32 16 32 7.16 24.84 0 16 0z" fill={color} />
      <circle cx="16" cy="16" r="10" fill="rgba(0,0,0,0.18)" />
      <g transform="translate(8,8)" style={{ color: "#fff" }}>
        <CatIcon catId={catId} size={16} />
      </g>
    </svg>
  );
}

/* ── Cluster bubble ───────────────────────────────────────── */
function ClusterMarker({ count, onClick }) {
  const size = count < 10 ? 38 : count < 50 ? 46 : 56;
  return (
    <button className="cmap__cluster" style={{ width: size, height: size }} onClick={onClick} aria-label={`Cluster of ${count} participants`}>
      <span className="cmap__cluster-num">{count}</span>
    </button>
  );
}

/* ── Story side panel ─────────────────────────────────────── */
function StoryPanel({ participant, onClose }) {
  const p = participant?.properties;
  if (!p) return null;
  const color = CAT_COLORS[p.category] || "#5f7158";
  const catLabel = CATEGORIES.find((c) => c.id === p.category)?.label || p.category;

  return (
    <AnimatePresence>
      <motion.aside
        className="cmap__panel"
        key={p.id}
        initial={{ x: "110%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "110%", opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        role="dialog"
        aria-label={`Story of ${p.name}`}
      >
        {/* Portrait area */}
        <div className="cmap__panel-portrait">
          {p.portrait ? (
            <img src={p.portrait} alt={p.name} />
          ) : (
            <div className="cmap__panel-portrait-placeholder">
              <svg viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="32" r="18" fill="rgba(255,255,255,0.15)" />
                <path d="M10 76c0-16.57 13.43-30 30-30s30 13.43 30 30" fill="rgba(255,255,255,0.1)" />
              </svg>
            </div>
          )}
          {/* Close button */}
          <button className="cmap__panel-close" onClick={onClose} aria-label="Close story">
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="cmap__panel-body">
          {/* Category badge */}
          <div className="cmap__panel-cat" style={{ color }}>
            <span className="cmap__panel-cat-icon" style={{ color }}>
              <CatIcon catId={p.category} size={14} />
            </span>
            <span className="cmap__panel-cat-label">{catLabel.toUpperCase()}</span>
          </div>

          {/* Name */}
          <h3 className="cmap__panel-name u-display">{p.name}</h3>

          {/* Meta */}
          <div className="cmap__panel-meta">
            <span>{p.city}</span>
            <span className="cmap__panel-meta-dot">·</span>
            <span>{p.retreat}</span>
          </div>

          {/* Stars */}
          <Stars rating={p.rating} />

          {/* Botanical divider */}
          <div className="cmap__panel-divider" aria-hidden="true" />

          {/* Quote */}
          <blockquote className="cmap__panel-quote">"{p.quote}"</blockquote>

          {/* CTA */}
          <a href={p.storyUrl} className="cmap__panel-cta">
            View Full Story <FaArrowRight className="cmap__panel-cta-icon" />
          </a>

          {/* Botanical SVG watermark */}
          <div className="cmap__panel-botanical" aria-hidden="true">
            <img src="./assets/plant.png" alt="" />
          </div>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

/* ── Filter bar ───────────────────────────────────────────── */
function FilterBar({ active, onChange }) {
  return (
    <nav className="cmap__filters" aria-label="Filter by participant type">
      {CATEGORIES.map((cat) => (
        <button key={cat.id} className={`cmap__filter-btn ${active === cat.id ? "cmap__filter-btn--active" : ""}`} onClick={() => onChange(cat.id)}>
          {cat.id !== "all" && (
            <span className="cmap__filter-icon" style={{ color: active === cat.id ? "#fff" : CAT_COLORS[cat.id] }}>
              <CatIcon catId={cat.id} size={15} />
            </span>
          )}
          {cat.label}
        </button>
      ))}
    </nav>
  );
}

/* ── Map controls overlay ─────────────────────────────────── */
function MapControls({ onReset }) {
  const { current: map } = useMap();
  return (
    <div className="cmap__controls">
      <button className="cmap__ctrl-btn" onClick={() => map?.zoomIn()} aria-label="Zoom in">
        +
      </button>
      <button className="cmap__ctrl-btn" onClick={() => map?.zoomOut()} aria-label="Zoom out">
        −
      </button>
      <button className="cmap__ctrl-btn" onClick={onReset} aria-label="Reset view">
        <FaCrosshairs />
      </button>
    </div>
  );
}

/* ── Cluster view legend ──────────────────────────────────── */
function ClusterLegend() {
  return (
    <div className="cmap__legend">
      <BsLayers className="cmap__legend-icon" />
      <div>
        <p className="cmap__legend-title">Cluster View</p>
        <p className="cmap__legend-sub">Zoom in to explore more stories.</p>
      </div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────── */
export default function CommunityMap() {
  const [PARTICIPANTS, setParticipants] = useState([]);

  useEffect(() => {
    storiesAPI
      .list()
      .then((res) => {
        const rows = res.data || [];
        const features = rows.map((r) => ({
          id: r.id,
          type: "Feature",
          geometry: { type: "Point", coordinates: [parseFloat(r.lng), parseFloat(r.lat)] },
          properties: {
            id: r.id,
            category: r.category,
            name: `${r.first_name}${r.last_name_initial ? " " + r.last_name_initial + "." : ""}`,
            city: r.city,
            retreat: r.retreat_name,
            rating: r.rating,
            quote: r.quote,
          },
        }));
        setParticipants(features);
      })
      .catch(() => {
        import("../../data/mockParticipants").then((m) => setParticipants(m.default));
      });
  }, []);

  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [clusters, setClusters] = useState([]);
  const mapRef = useRef(null);
  const bodyRef = useRef(null);

  // useEffect(() => {
  //   const el = bodyRef.current;
  //   if (!el) return;

  //   const enter = () => {
  //     if (window.__lenis) {
  //       window.__lenis.stop();
  //     }
  //   };

  //   const leave = () => {
  //     if (window.__lenis) {
  //       window.__lenis.start();
  //     }
  //   };

  //   el.addEventListener("mouseenter", enter);
  //   el.addEventListener("mouseleave", leave);

  //   return () => {
  //     el.removeEventListener("mouseenter", enter);
  //     el.removeEventListener("mouseleave", leave);
  //   };
  // }, []);

  /* Filtered participants */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => (activeFilter === "all" ? PARTICIPANTS : PARTICIPANTS.filter((p) => p.properties.category === activeFilter)), [activeFilter, PARTICIPANTS]);

  /* Supercluster instance — rebuild when filter changes */
  const supercluster = useMemo(() => {
    const sc = new Supercluster({ radius: 55, maxZoom: 14, minPoints: 2 });
    sc.load(filtered);
    return sc;
  }, [filtered]);

  /* Recompute clusters on move/zoom */
  const updateClusters = useCallback(() => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds) return;
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    const zoom = Math.round(viewState.zoom);
    setClusters(supercluster.getClusters(bbox, zoom));
  }, [supercluster, viewState.zoom]);

  useEffect(() => {
    updateClusters();
  }, [updateClusters]);

  /* Close panel when filter changes */
  useEffect(() => {
    setSelectedPoint(null);
  }, [activeFilter]);

  /* Expand cluster on click */
  const handleClusterClick = useCallback(
    (clusterId, lng, lat) => {
      const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(clusterId), 16);
      setViewState((v) => ({ ...v, longitude: lng, latitude: lat, zoom: expansionZoom }));
    },
    [supercluster],
  );

  /* Reset view */
  const handleReset = useCallback(() => {
    setViewState(INITIAL_VIEW);
    setSelectedPoint(null);
  }, []);

  /* Total count */
  const totalLives = 1247;

  return (
    <section className="cmap" id="community">
      {/* ── Header bar ── */}
      <div className="cmap__header">
        <div className="cmap__header-left">
          <h2 className="cmap__header-title u-display">Community Impact Map</h2>
          <span className="cmap__header-rule" aria-hidden="true" />
          <p className="cmap__header-sub">
            Every pin represents someone whose life
            <br />
            Forge has touched.
          </p>
        </div>

        <div className="cmap__header-right">
          <div className="cmap__header-count">
            <span className="cmap__header-num">{totalLives.toLocaleString()}</span>
            <span className="cmap__header-count-label">
              LIVES IMPACTED
              <br />
              AND GROWING
            </span>
          </div>
          <CircularBadge colors="var(--c-rust)" logoSize={28} />
        </div>
      </div>

      {/* ── Filter bar ── */}
      <FilterBar active={activeFilter} onChange={setActiveFilter} />

      {/* ── Map + panel wrapper ── */}
      <div className="cmap__body" ref={bodyRef}>
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(e) => setViewState(e.viewState)}
          onMoveEnd={updateClusters}
          mapStyle={MAP_STYLE}
          style={{ width: "100%", height: "100%" }}
          attributionControl={false}
          minZoom={2}
          maxZoom={16}
        >
          {/* Custom controls (hides default MapLibre controls) */}
          <MapControls onReset={handleReset} />

          {/* Markers + clusters */}
          {clusters.map((feature) => {
            const [lng, lat] = feature.geometry.coordinates;
            const { cluster, point_count, cluster_id } = feature.properties;

            if (cluster) {
              return (
                <Marker key={`cluster-${cluster_id}`} longitude={lng} latitude={lat} anchor="center">
                  <ClusterMarker count={point_count} onClick={() => handleClusterClick(cluster_id, lng, lat)} />
                </Marker>
              );
            }

            /* Individual pin */
            const { category, id } = feature.properties;
            const color = CAT_COLORS[category] || "#5f7158";
            const isSelected = selectedPoint?.properties?.id === id;

            return (
              <Marker
                key={`pin-${id}`}
                longitude={lng}
                latitude={lat}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  setSelectedPoint(feature);
                }}
              >
                <motion.div
                  className={`cmap__pin-wrap ${isSelected ? "cmap__pin-wrap--selected" : ""}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  style={{ cursor: "pointer" }}
                >
                  <Pin color={color} catId={category} size={isSelected ? 38 : 30} />
                </motion.div>
              </Marker>
            );
          })}
        </Map>

        {/* Cluster view legend — bottom left */}
        <ClusterLegend />

        {/* Story panel — right side */}
        {selectedPoint && <StoryPanel participant={selectedPoint} onClose={() => setSelectedPoint(null)} />}
      </div>
    </section>
  );
}
